import React from 'react';

import Validation, { Form } from 'modules/react-validation';

import ParameterSet, { ParameterGroupProvider } from 'sharedreact/components/ParameterSet';
import SegmentedButtonField from 'sharedreact/components/SegmentedButtonField';
import TextField from 'sharedreact/components/TextField';
import hideable from 'sharedreact/components/hideable';
import { translate } from 'sharedreact/utils/LanguageSupport';
import ValidationRules from 'sharedreact/utils/ValidationRules';

import OpenIDAzureConfigPanel from 'workspaces/launcher/components/admin/OpenIDAzureConfigPanel';
import OpenIDOktaConfigPanel from 'workspaces/launcher/components/admin/OpenIDOktaConfigPanel';
import OpenIDConfig from 'workspaces/launcher/data/OpenIDConfig';
import { SegmentType } from 'sharedreact/components/SegmentedButton';
import Dialog from 'sharedreact/components/Dialog';

type Props = {
  show: boolean,
  language: string,
  openIDConfig: OpenIDConfig | null,

  onClose: () => void,
  onSave: (openIdConfig: OpenIDConfig) => void,
  onChangeValue: (name: string, value: any) => void
};

type State = {
  isDirty: boolean
};

function _displayInvalidName (): string {
  return translate('launcher.identityproviders.oidcconfig.err.illegalname');
}

function hasFormErrors (form: any): boolean {
  return (form != null && Object.keys(form.getErrors()).length > 0);
}

class EditOpenIDConfigDialog extends React.Component<Props, State> {
  private _form: Form | null;
  private _oktaForm: Form | null;
  private _azureForm: Form | null;
  
  private _edited: string[];
  private _generalRequired: string[];
  private _oktaRequired: string[];
  private _azureRequired: string[];

  constructor (props: Props) {
    super(props);

    this.state = {
      isDirty: false
    };

    this._edited = [];
    this._generalRequired = ['name'];
    this._oktaRequired = ['oktahost', 'clientId', 'clientSecret'];
    this._azureRequired = ['tenantId', 'clientId', 'clientSecret'];

    this._form = null;
    this._oktaForm = null;
    this._azureForm = null;

    this.handleRequiredChange = this.handleRequiredChange.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleValidate = this.handleValidate.bind(this);
  }

  override componentDidMount (): void {
    if (this.props.openIDConfig != null) {
      const openIDConfig: OpenIDConfig = this.props.openIDConfig;
      this._edited = [];

      if (openIDConfig.name !== '') {
        this._edited.push('name');
      }
      if (openIDConfig.clientId !== '') {
        this._edited.push('clientId');
      }
      if (openIDConfig.clientSecret !== '' ||
        openIDConfig.id !== '' // id !== '' means this is a saved config and
                               // and should always have a clientSecret
        ) {
        this._edited.push('clientSecret');
      }
      if (openIDConfig.oktahost !== '') {
        this._edited.push('oktahost');
      }
      if (openIDConfig.tenantId !== '') {
        this._edited.push('tenantId');
      }
    }
  }

  clearDirty (): void {
    this.setState({'isDirty': false});
  }

  override componentDidUpdate (prevProps: Readonly<Props>): void {
    if (this.props.openIDConfig != null && prevProps.openIDConfig == null) {
      const openIDConfig: OpenIDConfig = this.props.openIDConfig;
      this._edited = [];
      this.clearDirty();

      if (openIDConfig.name !== '') {
        this._edited.push('name');
      }
      if (openIDConfig.clientId) {
        this._edited.push('clientId');
      }
      if (openIDConfig.clientSecret !== '' ||
        openIDConfig.id !== '' // id !== '' means this is a saved config and
                              // and should always have a clientSecret
      ) {
        this._edited.push('clientSecret');
      }
      if (openIDConfig.oktahost !== '') {
        this._edited.push('oktahost');
      }
      if (openIDConfig.tenantId !== '') {
        this._edited.push('tenantId');
      }
    }

    if (this.props.openIDConfig != null && prevProps.openIDConfig != null &&
      this.props.openIDConfig.type !== prevProps.openIDConfig.type) {
      setTimeout(() => {
        this._validateEdited();
        this.forceUpdate(); // make sure that ok button enabled state is updated
      });
    }
  }

  handleRequiredChange (name: string, value: any): void {
    if (!this._edited.includes(name)) {
      this._edited.push(name);
    }

    if (!this.state.isDirty) {
      this.setState({isDirty: true});
    }

    this.props.onChangeValue(name, value);
  }

  handleChange (name: string, value: any): void {
    if (!this.state.isDirty) {
      this.setState({isDirty: true});
    }

    this.props.onChangeValue(name, value);
  }

  _validateEdited () {
    this._edited.filter(name => this._generalRequired.includes(name)).forEach(name => this._form!.validate(name));

    const isOkta: boolean = this.props.openIDConfig != null && this.props.openIDConfig.isOkta();
    if (isOkta) {
      this._edited.filter(name => this._oktaRequired.includes(name)).forEach(name => this._oktaForm!.validate(name));
    } else {
      this._edited.filter(name => this._azureRequired.includes(name)).forEach(name => this._azureForm!.validate(name));
    }
  }

  handleSubmit () {
    if (this.props.openIDConfig != null) {
      const openIDConfig: OpenIDConfig = this.props.openIDConfig;
      this._form!.validateAll();

      let subformHasErrors: boolean = false;
      const isOkta: boolean = openIDConfig.isOkta();
      if (isOkta) {
        this._oktaForm!.validateAll();
        subformHasErrors = hasFormErrors(this._oktaForm);
      } else {
        this._azureForm!.validateAll();
        subformHasErrors = hasFormErrors(this._azureForm);
      }

      if (!hasFormErrors(this._form) && !subformHasErrors) {
        this.props.onSave(openIDConfig);
        this.props.onClose();
      }
    }
  }

  handleValidate (name: string) {
    this._form!.validate(name);
  }

  override render (): React.ReactNode {
    const { show, openIDConfig, onClose, language } = this.props;

    if (openIDConfig == null) {
      return null;
    }

    const isOkta: boolean = openIDConfig.isOkta();

    const allElementsEdited: boolean = (
      this._edited.filter(name => this._generalRequired.includes(name)).length === this._generalRequired.length &&
      (isOkta
        ? this._edited.filter(name => this._oktaRequired.includes(name)).length === this._oktaRequired.length
        : this._edited.filter(name => this._azureRequired.includes(name)).length === this._azureRequired.length
      )
    );

    const okDisabled: boolean = (
      !this.state.isDirty ||
      !allElementsEdited ||
      hasFormErrors(this._form) ||
      (isOkta
        ? hasFormErrors(this._oktaForm)
        : hasFormErrors(this._azureForm)
      )
    );

    const submit = (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.preventDefault();
      this.handleSubmit();
    };

    const segmentsType: SegmentType[] = [
      { id: 'azure',
        text: translate('launcher.identityproviders.oidc.azure')
      },
      { id: 'okta',
        text: translate('launcher.identityproviders.oidc.okta')
      }
    ];

    const nameRule = ValidationRules.addCustomRule('input_openIDName', (value) => !value.match(/[ #%&|\\*?=:/@'"`{}[\]<>()]/), _displayInvalidName);
    const nameValidations = ['required'].concat(nameRule);

    const isNew: boolean = openIDConfig.isNew();

    return (
      <Dialog
        id='sa_dialog_openidconfig'
        show={show}
        language={language}
        title={translate(isNew ? 'launcher.identityproviders.oidcconfig.addtitle' : 'launcher.identityproviders.oidcconfig.edittitle')}
        onClose={onClose}
        onConfirm={submit}
        disableConfirm={okDisabled}
        primaryButton='confirm'
      >
        <ParameterGroupProvider>
          <Validation.components.Form
            ref={c => { this._form = c; }}>
            <ParameterSet useProvider={false}>
              <TextField id='ae_text_identityproviders_openid_name'
                name='name'
                value={openIDConfig.name}
                label={translate('launcher.identityproviders.oidc.name')}
                onChangeValue={this.handleRequiredChange}
                validations={nameValidations}
                validate={this.handleValidate}
              />
              <SegmentedButtonField id='ae_segment_identityproviders_openid_type'
                name='type'
                className='esko_parameter-input-group-max500'
                value={openIDConfig.type}
                label={translate('launcher.identityproviders.oidc.type')}
                segments={segmentsType}
                onChangeValue={this.handleChange}
              />
            </ParameterSet>
          </Validation.components.Form>
          {isOkta
          ? <OpenIDOktaConfigPanel openIDConfig={openIDConfig}
            onChangeValue={this.handleRequiredChange}
            onValidate={() => {}}
            setForm={(formElement) => { this._oktaForm = formElement; }}
          />
          : <OpenIDAzureConfigPanel openIDConfig={openIDConfig}
            onChangeValue={this.handleRequiredChange}
            onValidate={() => {}}
            setForm={(formElement) => { this._azureForm = formElement; }}
          />}
        </ParameterGroupProvider>
      </Dialog>
    );
  }
}

export default hideable(EditOpenIDConfigDialog);
